import React, { useState } from 'react';
import Header from '../../components/ui/Header';
import SessionManager from '../../components/ui/SessionManager';
import UniversalSidePanel from './components/UniversalSidePanel';
import Icon from '../../components/AppIcon';

const UniversalSidePanelDetailSystem = () => {
  const [isPanelOpen, setIsPanelOpen] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [recordType, setRecordType] = useState('property');

  const mockRecords = {
    property: [
      {
        id: 'PROP-001',
        type: 'property',
        title: '45 Park Avenue',
        address: '45 Park Avenue, London, SW1A 1AA',
        landlord: 'John Smith',
        tenant: 'Sarah Johnson',
        monthlyRent: 2500,
        status: 'Occupied',
        tenancyStart: '2024-01-15',
        tenancyEnd: '2025-01-14',
        documents: [
          { id: 1, name: 'Tenancy Agreement.pdf', type: 'pdf', size: '2.4 MB', uploadDate: '2024-01-10', status: 'Verified' },
          { id: 2, name: 'Property Photos.jpg', type: 'image', size: '1.8 MB', uploadDate: '2024-01-12', status: 'Uploaded' }
        ],
        notes: 'Property in excellent condition. Tenant has been reliable with rent payments.'
      },
      {
        id: 'PROP-002',
        type: 'property',
        title: '12 Baker Street',
        address: '12 Baker Street, London, NW1 6XE',
        landlord: 'Emma Wilson',
        tenant: 'Michael Brown',
        monthlyRent: 3200,
        status: 'Occupied',
        tenancyStart: '2023-06-01',
        tenancyEnd: '2024-05-31',
        documents: [
          { id: 3, name: 'EPC Certificate.pdf', type: 'pdf', size: '890 KB', uploadDate: '2023-05-20', status: 'Verified' }
        ],
        notes: 'Renewal discussion scheduled for next month.'
      }
    ],
    certificate: [
      {
        id: 'CERT-GAS-001',
        type: 'certificate',
        title: 'Gas Safety Certificate',
        propertyId: 'PROP-001',
        address: '45 Park Avenue, London, SW1A 1AA',
        certType: 'Gas Safety',
        issueDate: '2024-01-15',
        expiryDate: '2025-01-14',
        daysUntilExpiry: 332,
        contractor: 'ABC Gas Services',
        documents: [
          { id: 4, name: 'Gas Safety Certificate.pdf', type: 'pdf', size: '1.2 MB', uploadDate: '2024-01-15', status: 'Verified' }
        ],
        notes: 'All appliances passed inspection. Next inspection due January 2025.'
      }
    ],
    maintenance: [
      {
        id: 'MAINT-001',
        type: 'maintenance',
        title: 'Boiler Repair',
        propertyId: 'PROP-001',
        address: '45 Park Avenue, London, SW1A 1AA',
        trade: 'Heating',
        priority: 'urgent',
        status: 'In Progress',
        contractor: 'ABC Heating Ltd',
        cost: 450,
        createdDate: '2024-02-10',
        documents: [
          { id: 5, name: 'Boiler Issue Photo.jpg', type: 'image', size: '2.1 MB', uploadDate: '2024-02-10', status: 'Uploaded' }
        ],
        notes: 'Tenant reported no heating. Contractor scheduled for tomorrow.'
      }
    ],
    tenancy: [
      {
        id: 'TEN-001',
        type: 'tenancy',
        title: 'Sarah Johnson - 45 Park Avenue',
        tenantName: 'Sarah Johnson',
        email: 'sarah.johnson@email.com',
        phone: '07700 900123',
        propertyId: 'PROP-001',
        address: '45 Park Avenue, London, SW1A 1AA',
        moveInDate: '2024-01-15',
        depositProtected: 'Yes',
        documents: [
          { id: 6, name: 'ID Verification.pdf', type: 'pdf', size: '1.5 MB', uploadDate: '2024-01-05', status: 'Verified' },
          { id: 7, name: 'Lease Agreement.pdf', type: 'pdf', size: '3.2 MB', uploadDate: '2024-01-10', status: 'Verified' },
          { id: 8, name: 'Consent Forms.pdf', type: 'pdf', size: '890 KB', uploadDate: '2024-01-08', status: 'Missing' }
        ],
        notes: 'All documents submitted on time. Excellent tenant.'
      }
    ]
  };

  const handleRecordClick = (record, type) => {
    setSelectedRecord(record);
    setRecordType(type);
    setIsPanelOpen(true);
  };

  const handleClosePanel = () => {
    setIsPanelOpen(false);
    setTimeout(() => {
      setSelectedRecord(null);
    }, 300);
  };

  return (
    <>
      <SessionManager />
      <Header />
      <div className="pt-[108px] min-h-screen bg-background">
        <div className="max-w-7xl mx-auto p-6">
          <div className="mb-6">
            <h1 className="text-3xl font-bold text-foreground mb-2">Universal Side Panel System</h1>
            <p className="text-muted-foreground">Click any record to open the contextual detail panel</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="card">
              <div className="flex items-center gap-3 mb-4">
                <Icon name="Building2" size={24} color="var(--color-accent)" />
                <h2 className="text-xl font-semibold text-foreground">Properties</h2>
              </div>
              <div className="space-y-3">
                {mockRecords?.property?.map((record) => (
                  <button
                    key={record?.id}
                    onClick={() => handleRecordClick(record, 'property')}
                    className="w-full text-left p-4 rounded-lg bg-input border border-border hover:border-accent hover:bg-accent/5 transition-smooth"
                  >
                    <div className="flex items-start justify-between">
                      <div>
                        <p className="font-medium text-foreground">{record?.title}</p>
                        <p className="text-sm text-muted-foreground mt-1">{record?.id}</p>
                      </div>
                      <Icon name="ChevronRight" size={20} color="var(--color-muted-foreground)" />
                    </div>
                  </button>
                ))}
              </div>
            </div>

            <div className="card">
              <div className="flex items-center gap-3 mb-4">
                <Icon name="ShieldCheck" size={24} color="var(--color-warning)" />
                <h2 className="text-xl font-semibold text-foreground">Certificates</h2>
              </div>
              <div className="space-y-3">
                {mockRecords?.certificate?.map((record) => (
                  <button
                    key={record?.id}
                    onClick={() => handleRecordClick(record, 'certificate')}
                    className="w-full text-left p-4 rounded-lg bg-input border border-border hover:border-accent hover:bg-accent/5 transition-smooth"
                  >
                    <div className="flex items-start justify-between">
                      <div>
                        <p className="font-medium text-foreground">{record?.title}</p>
                        <p className="text-sm text-muted-foreground mt-1">{record?.id}</p>
                      </div>
                      <Icon name="ChevronRight" size={20} color="var(--color-muted-foreground)" />
                    </div>
                  </button>
                ))}
              </div>
            </div>

            <div className="card">
              <div className="flex items-center gap-3 mb-4">
                <Icon name="Wrench" size={24} color="var(--color-error)" />
                <h2 className="text-xl font-semibold text-foreground">Maintenance</h2>
              </div>
              <div className="space-y-3">
                {mockRecords?.maintenance?.map((record) => (
                  <button
                    key={record?.id}
                    onClick={() => handleRecordClick(record, 'maintenance')}
                    className="w-full text-left p-4 rounded-lg bg-input border border-border hover:border-accent hover:bg-accent/5 transition-smooth"
                  >
                    <div className="flex items-start justify-between">
                      <div>
                        <p className="font-medium text-foreground">{record?.title}</p>
                        <p className="text-sm text-muted-foreground mt-1">{record?.id}</p>
                      </div>
                      <Icon name="ChevronRight" size={20} color="var(--color-muted-foreground)" />
                    </div>
                  </button>
                ))}
              </div>
            </div>

            <div className="card">
              <div className="flex items-center gap-3 mb-4">
                <Icon name="Users" size={24} color="var(--color-success)" />
                <h2 className="text-xl font-semibold text-foreground">Tenancies</h2>
              </div>
              <div className="space-y-3">
                {mockRecords?.tenancy?.map((record) => (
                  <button
                    key={record?.id}
                    onClick={() => handleRecordClick(record, 'tenancy')}
                    className="w-full text-left p-4 rounded-lg bg-input border border-border hover:border-accent hover:bg-accent/5 transition-smooth"
                  >
                    <div className="flex items-start justify-between">
                      <div>
                        <p className="font-medium text-foreground">{record?.title}</p>
                        <p className="text-sm text-muted-foreground mt-1">{record?.id}</p>
                      </div>
                      <Icon name="ChevronRight" size={20} color="var(--color-muted-foreground)" />
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      <UniversalSidePanel
        isOpen={isPanelOpen}
        onClose={handleClosePanel}
        record={selectedRecord}
        recordType={recordType}
      />
    </>
  );
};

export default UniversalSidePanelDetailSystem;